
import React, { useState, useEffect, useRef } from 'react';
import { IconChat, IconHash, IconAtSign, IconPaperAirplane, IconSearch, IconSparkles, IconQuant } from '../components/Icons';
import { summarizeChatHistory } from '../services/geminiService';
import { useAiQ } from '../state/AiQState';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';

interface ChatMessage {
    id: string;
    sender: string;
    avatar: string;
    text: string;
    timestamp: string;
    type: 'text' | 'alert' | 'trade';
    metadata?: any;
}

interface Channel {
    id: string;
    name: string;
    unread: number;
    type: 'public' | 'private';
}

const CHANNELS: Channel[] = [
    { id: 'C1', name: 'global-macro', unread: 3, type: 'public' },
    { id: 'C2', name: 'equity-desk', unread: 0, type: 'public' },
    { id: 'C3', name: 'risk-alerts', unread: 12, type: 'public' },
    { id: 'C4', name: 'ops-exceptions', unread: 0, type: 'private' },
];

const MOCK_MESSAGES: ChatMessage[] = [
    { id: 'M1', sender: 'Sarah (Risk)', avatar: 'SJ', text: 'Vol surface on $SPX is looking steep today. Skew is elevated.', timestamp: '09:15', type: 'text' },
    { id: 'M2', sender: 'Mike (Trader)', avatar: 'MD', text: 'Agreed. I am seeing flows into puts. Just booked #TRD-8832 to hedge our delta.', timestamp: '09:18', type: 'trade', metadata: { symbol: 'SPX', side: 'Buy', qty: 500 } },
    { id: 'M3', sender: 'System', avatar: 'AI', text: 'Limit Warning: JPM Counterparty exposure > 80%.', timestamp: '09:20', type: 'alert' },
    { id: 'M4', sender: 'David (PM)', avatar: 'DB', text: '@Mike check the $AAPL correlation. It might be breaking down against the index.', timestamp: '09:25', type: 'text' },
];

const CollaborationView: React.FC = () => {
    const [activeChannel, setActiveChannel] = useState(CHANNELS[0]);
    const [messages, setMessages] = useState<ChatMessage[]>(MOCK_MESSAGES);
    const [input, setInput] = useState('');
    const [summary, setSummary] = useState('');
    const [isSummarizing, setIsSummarizing] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = () => {
        if (!input.trim()) return;
        
        const newMsg: ChatMessage = {
            id: Date.now().toString(),
            sender: 'You',
            avatar: 'ME',
            text: input,
            timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
            type: 'text'
        };
        
        setMessages([...messages, newMsg]);
        setInput('');

        // Simulate AI Reply if mentioned
        if (input.includes('@AiQ')) {
            setTimeout(() => {
                setMessages(prev => [...prev, {
                    id: Date.now().toString(),
                    sender: 'AiQ Bot',
                    avatar: 'AI',
                    text: 'I am analyzing that query. Correlation matrix suggests a regime shift in Tech/Rates.',
                    timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
                    type: 'text'
                }]);
            }, 1500);
        }
    };

    const handleSummarize = async () => {
        setIsSummarizing(true);
        setSummary('');
        const result = await summarizeChatHistory(activeChannel.name, messages);
        setSummary(result);
        setIsSummarizing(false);
    };

    // Smart Tag Renderer
    const renderMessageText = (text: string) => {
        return text.split(/(\$[A-Z]+|#TRD-\d+|@\w+)/g).map((part, i) => {
            if (part.startsWith('$')) {
                return <span key={i} className="text-blue-600 font-bold cursor-pointer hover:underline bg-blue-50 px-1 rounded">{part}</span>;
            }
            if (part.startsWith('#')) {
                return <span key={i} className="text-purple-600 font-mono cursor-pointer hover:underline bg-purple-50 px-1 rounded">{part}</span>;
            }
            if (part.startsWith('@')) {
                return <span key={i} className="text-amber-600 font-bold bg-amber-50 px-1 rounded">{part}</span>;
            }
            return part;
        });
    };

    return (
        <div className="flex h-full bg-slate-100">
            {/* Left: Channels */}
            <div className="w-64 bg-slate-900 text-slate-300 flex flex-col border-r border-slate-800">
                <div className="p-4 border-b border-slate-800 font-bold flex items-center gap-2 text-white">
                    <IconChat /> Team Chat
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                    <div className="px-3 py-2 text-xs font-bold text-slate-500 uppercase">Channels</div>
                    {CHANNELS.map(ch => (
                        <div 
                            key={ch.id}
                            onClick={() => { setActiveChannel(ch); setSummary(''); }}
                            className={`px-3 py-2 rounded cursor-pointer flex justify-between items-center transition-colors ${activeChannel.id === ch.id ? 'bg-blue-600 text-white' : 'hover:bg-slate-800'}`}
                        >
                            <span className="flex items-center gap-2">
                                <span className="opacity-50"><IconHash /></span> 
                                {ch.name}
                            </span>
                            {ch.unread > 0 && (
                                <span className="bg-red-500 text-white text-[10px] px-1.5 rounded-full font-bold">{ch.unread}</span>
                            )}
                        </div>
                    ))}
                </div>
                <div className="p-4 border-t border-slate-800 text-xs text-slate-500">
                    <p className="flex items-center gap-2"><span className="w-2 h-2 bg-emerald-500 rounded-full"></span> Compliance Log Active</p>
                </div>
            </div>

            {/* Main: Chat Area */}
            <div className="flex-1 flex flex-col bg-white relative">
                {/* Header */}
                <div className="h-16 border-b border-slate-200 flex justify-between items-center px-6 shrink-0">
                    <div>
                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            <IconHash /> {activeChannel.name}
                        </h3>
                        <p className="text-xs text-slate-500">24 Members • Topic: Market Flows</p>
                    </div>
                    <div className="flex gap-3">
                        <div className="relative">
                            <input type="text" placeholder="Search messages..." className="pl-8 pr-3 py-1.5 bg-slate-100 rounded-full text-sm outline-none" />
                            <span className="absolute left-2.5 top-2 text-slate-400"><IconSearch /></span>
                        </div>
                        <button 
                            onClick={handleSummarize}
                            className="bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full text-sm font-bold hover:bg-purple-100 transition-colors flex items-center gap-2"
                        >
                            <IconSparkles /> Summarize
                        </button>
                    </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-slate-50">
                    {/* Summary Card */}
                    {summary && (
                        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6 animate-fade-in-up shadow-sm">
                            <div className="flex items-center gap-2 text-purple-800 font-bold text-sm mb-2">
                                <IconQuant /> AiQ Catch-Up Summary
                            </div>
                            <div className="prose prose-sm text-purple-900 leading-relaxed">
                                {summary}
                            </div>
                            <div className="mt-2 text-right">
                                <button onClick={() => setSummary('')} className="text-xs text-purple-500 hover:underline">Dismiss</button>
                            </div>
                        </div>
                    )}

                    {isSummarizing && (
                        <div className="flex justify-center py-4">
                            <div className="flex items-center gap-2 text-slate-400 text-sm animate-pulse">
                                <IconSparkles /> Reading chat history...
                            </div>
                        </div>
                    )}

                    {messages.map(msg => (
                        <div key={msg.id} className={`flex gap-4 group ${msg.type === 'alert' ? 'bg-red-50 p-4 rounded-lg border border-red-100' : ''}`}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shrink-0 shadow-sm ${
                                msg.avatar === 'AI' ? 'bg-purple-600' : 
                                msg.avatar === 'ME' ? 'bg-blue-600' : 'bg-slate-400'
                            }`}>
                                {msg.avatar}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 mb-1">
                                    <span className="font-bold text-slate-800">{msg.sender}</span>
                                    <span className="text-xs text-slate-400">{msg.timestamp}</span>
                                    {msg.type === 'alert' && <span className="bg-red-100 text-red-600 text-[10px] px-1.5 rounded font-bold">SYSTEM ALERT</span>}
                                </div>
                                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                                    {renderMessageText(msg.text)}
                                </p>
                                
                                {/* Trade Card Attachment */}
                                {msg.type === 'trade' && msg.metadata && (
                                    <div className="mt-2 inline-block bg-white border border-slate-200 rounded p-2 shadow-sm">
                                        <div className="flex items-center gap-3 text-xs">
                                            <div className="bg-emerald-100 text-emerald-700 px-1.5 rounded font-bold">{msg.metadata.side.toUpperCase()}</div>
                                            <div className="font-bold text-slate-700">{msg.metadata.symbol}</div>
                                            <div className="text-slate-500">{msg.metadata.qty} units</div>
                                            {/* Tiny Mock Sparkline */}
                                            <div className="w-16 h-6">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={[{v:10}, {v:12}, {v:11}, {v:13}, {v:15}]}>
                                                        <Line type="monotone" dataKey="v" stroke="#10b981" strokeWidth={2} dot={false} />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 bg-white border-t border-slate-200">
                    <div className="relative flex items-center gap-2 bg-slate-50 p-2 rounded-xl border border-slate-200 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100 transition-all">
                        <button className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-200 transition-colors">
                            <IconAtSign />
                        </button>
                        <input 
                            type="text" 
                            className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder-slate-400"
                            placeholder={`Message #${activeChannel.name}... (Use $Ticker, @AiQ)`}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        />
                        <button 
                            onClick={handleSend}
                            disabled={!input.trim()}
                            className={`p-2 rounded-lg transition-all ${input.trim() ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-200 text-slate-400'}`}
                        >
                            <IconPaperAirplane />
                        </button>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-2 text-center">
                        Chat logs are archived for compliance (Reg 17a-4).
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CollaborationView;
